<template>
  <div>
    <Modal
      v-model="isModalVisible"
      :title="$t('onboarding.languageConflict.title')"
      :backdrop="'static'"
      :hide-close-button="true"
    >
      <!-- Modal language picker -->
      <div class="mb-3">
        <label class="form-label">
          {{ $t('onboarding.languageConflict.modalLanguageLabel') }}
        </label>
        <select class="form-select" v-model="modalLocale" @change="changeModalLanguage">
          <option v-for="(lang, code) in allLanguages" :key="code" :value="code">
            {{ lang.displayName }}
          </option>
        </select>
      </div>

      <!-- Explanation -->
      <div class="alert alert-warning mb-3">
        {{ $t('onboarding.languageConflict.explanation') }}
      </div>

      <!-- Current conflict display -->
      <div class="conflict-display mb-3">
        <div class="mb-2">
          {{ $t('onboarding.languageConflict.currentNative') }}:
          <strong>{{ conflictingLanguageName }}</strong>
        </div>
        <div>
          {{ $t('onboarding.languageConflict.learningLanguage') }}:
          <strong>{{ conflictingLanguageName }}</strong>
        </div>
      </div>

      <!-- Native language selector -->
      <div class="mb-3">
        <label class="form-label">
          {{ $t('onboarding.languageConflict.selectCorrectNative') }}
        </label>
        <select
          class="form-select"
          v-model="newNativeLanguage"
          :class="{ 'is-invalid': !newNativeLanguage }"
        >
          <option value="">{{ $t('onboarding.languageConflict.selectPlaceholder') }}</option>
          <option
            v-for="(lang, code) in availableNativeLanguages"
            :key="code"
            :value="lang.name"
          >
            {{ lang.displayName }}
          </option>
        </select>
      </div>

      <template #footer>
        <button
          class="btn btn-primary"
          @click="saveNativeLanguage"
          :disabled="!newNativeLanguage"
        >
          {{ $t('onboarding.languageConflict.save') }}
        </button>
      </template>
    </Modal>
  </div>
</template>

<script>
import { authStore } from "@/store/auth";
import Modal from "./Modal.vue";
import { getDatabase, ref, update } from "firebase/database";
import Languages from "@/data/Language";
import { mapLanguageNameToLocale, previewLocale } from "@/locales";

export default {
  name: "LanguageConflictModal",
  components: {
    Modal
  },
  props: {
    selectedLanguage: {
      type: String,
      required: true
    }
  },
  emits: ['resolved'],
  data() {
    return {
      isModalVisible: false,
      newNativeLanguage: "",
      modalLocale: this.$i18n.locale,
      // Set once the save has landed. From that point the locale belongs to
      // authStore.setNativeLanguage, not to this modal's preview, and the
      // unmount cleanup must keep its hands off it.
      hasSaved: false,
    };
  },
  computed: {
    allLanguages() {
      // Use nativeName so users can identify their language regardless of current UI locale
      const result = {};
      for (const [code, lang] of Object.entries(Languages)) {
        result[code] = { ...lang, displayName: lang.nativeName };
      }
      return result;
    },
    availableNativeLanguages() {
      // Filter out the learning language; use nativeName for the same reason
      const result = {};
      for (const [code, lang] of Object.entries(Languages)) {
        if (code !== this.selectedLanguage) {
          result[code] = { ...lang, displayName: lang.nativeName };
        }
      }
      return result;
    },
    conflictingLanguageName() {
      if (Languages[this.selectedLanguage]) {
        return Languages[this.selectedLanguage].nativeName;
      }
      return this.selectedLanguage;
    }
  },
  watch: {
    // data() snapshots the locale at mount, and with lazy bundles the modal can
    // mount before the initial chunk lands -- at which point the snapshot is the
    // en-us fallback. Track the real locale so the dropdown never reads English
    // while the rest of the UI is in another language.
    '$i18n.locale'(newLocale) {
      this.modalLocale = newLocale;
    }
  },
  methods: {
    // This dropdown re-renders the modal in a language the user can read. It is
    // a preview, not a preference: the account's nativeLanguage is unchanged
    // until saveNativeLanguage below writes it. Caching here is what left
    // preferredLocale pointing somewhere nativeLanguage did not whenever the
    // dropdown was touched and the modal was not submitted.
    //
    // Bundles are lazy, so the locale cannot be applied until its chunk lands.
    // Returns the promise so callers (and tests) can await the switch.
    async changeModalLanguage() {
      await previewLocale(this.modalLocale);
    },
    // The one write of nativeLanguage here, and therefore the one thing that
    // refreshes the preferredLocale cache -- via authStore.setNativeLanguage,
    // which derives the locale from the value that just landed in RTDB.
    saveNativeLanguage() {
      console.debug("Saving corrected native language:", this.newNativeLanguage);
      // Captured before the await: whoever is signed in when the write RESOLVES
      // may not be who it was written for, and applying it to the wrong account
      // is how one user's language ends up cached as another's.
      const forUserId = authStore.getUserId();
      const userRef = ref(getDatabase(), `users/${forUserId}`);
      update(userRef, {
        nativeLanguage: this.newNativeLanguage,
      })
        .then(() => {
          console.debug("Native language updated successfully");
          // Set before the store call, because that call unmounts this modal
          // via the emit below and beforeUnmount must already see it.
          this.hasSaved = true;
          // Update auth store with new language preference
          authStore.setNativeLanguage(this.newNativeLanguage, { forUserId });
          this.isModalVisible = false;
          // Emit resolved event to parent
          this.$emit('resolved');
        })
        .catch((error) => {
          console.error("Error updating native language:", error);
        });
    },
  },
  mounted() {
    this.isModalVisible = true;
  },
  beforeUnmount() {
    // A preview ends when the surface previewing it goes away. The user cannot
    // dismiss this modal (static backdrop, no close button), but the parent
    // unmounts it on navigation, and without this the UI would stay in a
    // language the account does not have for the rest of the session.
    //
    // Skipped after a save, and NOT because it would be a harmless no-op. A
    // save unmounts this modal from inside setNativeLanguage's own call stack,
    // while that call's locale chunk is still loading. Starting a second switch
    // here would supersede it, and a superseded switch does not write the
    // cache -- so the account's new language would go live with nothing
    // recording it, and the next boot would open in the old one.
    if (!this.hasSaved && authStore.nativeLanguage) {
      previewLocale(mapLanguageNameToLocale(authStore.nativeLanguage));
    }
  },
};
</script>

<style scoped>
.conflict-display {
  padding: 1rem;
  background-color: var(--al-surface-sunken);
  border-radius: var(--al-radius);
  border: 1px solid var(--al-border);
}
</style>
